import React, { createContext, useContext, useEffect, useState } from 'react';
import socketIOClient, { Socket } from 'socket.io-client';
import { configObj, ConfigObj } from "../config/globalConfig";
import LoadingPage from '../pages/loading/LoadingPage';
import { useAuth } from './AuthContext';
import { getReqUrl } from '../config/reqUrlConfig';

export type UIMessage = {
  message: string;
} & Record<string, any>;

export type ServerResponse<T = any> = {
  result: 'success' | 'error';
  data?: T;
  error?: string;
};

type SocketContextValue = {
  socket: Socket | null;
  connected: boolean;
  emit: <T = any>(message?: UIMessage) => Promise<ServerResponse<T>>;
};

export const ServerMessages = {
  getGeneralStatus: "getGeneralStatus",
  getAppInfo: "getAppInfo",
  getEntries: "getEntries",
  getReportEntriesPerMonth: "getReportEntriesPerMonth",
  getIrrelevantLicensePlates: "getIrrelevantLicensePlates",
  getImportantEvents: "getImportantEvents",
  resetImportantEvents: "resetImportantEvents",
  getEntryExceptions: "getEntryExceptions",
  resetEntryExceptions: "resetEntryExceptions",
  createAlert: "createAlert",
  updateAlert: "updateAlert",
  deleteAlert: "deleteAlert",
  getAllAlerts: "getAllAlerts",
  markIrrelevant: "markIrrelevant",
  getReportCustomerTypes: "getReportCustomerTypes",
  enableOfflineNotification: "enableOfflineNotification",
  readIsOfflineNotificationEnabled: "readIsOfflineNotificationEnabled",
  getLocationAlerts: "getLocationAlerts",
  getReportTopFiftyClients: "getReportTop50Customers",
  getReportQuarterlyCustomers: "getReportQuarterlyCustomers",
  getReportYearComparisons: "getReportYearComparisons",
  getReportCustomerTypesComparison: "getReportCustomerTypesComparison",
  getReportMonthlyRevenue: "getReportMonthlyRevenue",
  deleteEntry: "deleteEntry",
  deleteAllEntriesForLicensePlate: "deleteAllEntriesForLicensePlate",
  updateMonthlyRevenue: "updateMonthlyRevenue",
  getReportSMSLog: "getReportSMSLog",
  listStaff: "listStaff",
  createStaff: "createStaff",
  setStaffRoleLevel: "setStaffRoleLevel",
  deleteStaff: "deleteStaff",
  updateStaff: "updateStaff",
  triggerBarrier: "triggerBarrier",
  createBarrier: "createBarrier",
  updateBarrier: "updateBarrier",
  deleteBarrier: "deleteBarrier",
  getAllBarriers: "getAllBarriers",
  updateGeneralDetails: "updateGeneralDetails",
  getWorkingHours: "getWorkingHours",
  updateWorkingHours: "updateWorkingHours",
  getOldestEntry: "getOldestEntry",
  getReportCarsPerStationComparison: "getReportCarsPerStationComparison",
};

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  connected: false,
  emit: async <T = any>(): Promise<ServerResponse<T>> => {
    // Default implementation for the context before a provider is mounted.
    return Promise.resolve({}) as Promise<ServerResponse<T>>;
  },
});

export const kUIMsgIdentifier = "uiMsg";
export const kSrvMsgIdentifier = "srvMsg";

export const EventTypes = {
  LIVE_EVENT: 'LiveEvent',
};

export const EventSubtypes = {
  ANPR_ENTRY: 'ANPREntry',
  CHECK_IN: 'Checkin',
  CHECK_OUT: 'Checkout',
}

export const SocketProvider: React.FC<{ _configObj?: ConfigObj, children?: React.ReactNode }> = ({ _configObj, children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const { isAuthenticated, activeLocationId } = useAuth();

  if (!_configObj) {
    _configObj = configObj; // fallback to global config
  }

  configObj.debug = Boolean(_configObj.debug);
  configObj.isStage = Boolean(_configObj.isStage);

  const URL = getReqUrl(_configObj);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    // create a socket instance directly for the whole app
    const s = socketIOClient(URL, { transports: ['websocket'] });
    setSocket(s);

    // update connected state on socket events so the provider can re-render
    const onConnect = () => {
      configObj.debug && console.log('SocketContext: connected');
      setConnected(true);
    };
    const onDisconnect = (reason?: string) => {
      configObj.debug && console.log('SocketContext: disconnected', reason);
      setConnected(false);
    };

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);

    // if socket already connected, set state
    if (s.connected) {
      setConnected(true);
    }

    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [URL, isAuthenticated]);

  const emit = async <T = any>(message?: UIMessage): Promise<ServerResponse<T>> => {
    if (!socket) {
      throw new Error('No socket instance available');
    }

    return new Promise<ServerResponse<T>>((resolve, reject) => {
      socket.emit(
        kUIMsgIdentifier,
        // todo use actual locatinId
        { ...message, locationId: activeLocationId },
        (response: ServerResponse<T>) => {
          if (response?.result === 'success') return resolve(response);
          // Reject with the full response when it's an error so callers can inspect it.
          return reject(response ?? new Error('Unknown error'));
        });
    });
  }

  // Render nothing until socket exists; once socket is present we render the provider
  // Children can optionally check socket.connected or use `connected` via context if exposed.
  if ((!socket || !connected) && isAuthenticated) {
    configObj.debug && console.log('SocketContext: no socket yet or not connected', socket, connected);
    return <LoadingPage />;
  }

  return <SocketContext.Provider value={{ socket, connected, emit }}>{children}</SocketContext.Provider>;
};

export const useSocketContext = () => useContext(SocketContext);

export default SocketContext;
